'use client';
import { ERR_INTERNAL_500 } from '@korvo/core/lib/error-codes';
import { KorvoError } from '@korvo/core/lib/korvo-error';
import { normalizeError } from '@korvo/core/lib/normalize-error';
import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';

export type ModulePageLoaderProps = {
    getModuleImport: (moduleName: string) => Promise<Record<string, React.ComponentType>>;
    onModuleNotFound?: (module: string) => void;
    onModuleDisabled?: (module: string) => void;
}
function LocalLoadingOverlay({ message }: {
    message: string;
}) {
    return (<div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--drawer-overlay-background)',
            backdropFilter: 'blur(4px)',
        }}>
      <div style={{
            padding: '1rem 1.5rem',
            borderRadius: '0.5rem',
            backgroundColor: 'white',
            boxShadow: '0 10px 25px rgba(15, 23, 42, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontSize: '0.875rem',
            color: 'var(--footer-foreground)',
        }}>
        <span style={{
            width: '1.25rem',
            height: '1.25rem',
            borderRadius: '9999px',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: 'var(--primary-default)',
            borderTopColor: 'transparent',
            animation: 'spin 0.8s linear infinite',
        }}/>
        <span>{message}</span>
      </div>
    </div>);
}
export function ModulePageLoader({ getModuleImport, onModuleNotFound, onModuleDisabled, }: ModulePageLoaderProps) {
    const params = useParams();
    const module = params?.module as string;
    const [isEnabled, setIsEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [ModuleComponent, setModuleComponent] = useState<React.ComponentType | null>(null);
    useEffect(() => {
        if (!module)
            return;
        let cancelled = false;
        const checkModuleEnabled = async () => {
            try {
                const res = await fetch('/api/env-status');
                const contentType = res.headers.get('content-type');
                if (!res.ok || contentType === null || !contentType.includes('application/json')) {
                    throw new KorvoError(ERR_INTERNAL_500, 1, {
                        message: `env-status returned non-JSON or error status: ${res.status}`,
                        status: res.status,
                    });
                }
                const data = await res.json();
                if (cancelled)
                    return;
                const moduleEnabled = data.modules?.[module.toLowerCase()] || false;
                setIsEnabled(moduleEnabled);
                if (moduleEnabled) {
                    void loadModuleComponent(module.toLowerCase());
                }
                else {
                    setIsLoading(false);
                    onModuleDisabled?.(module);
                }
            }
            catch (error: unknown) {
                const errorNormalized = normalizeError(error);
                console.errorNormalized('Failed to load env-status for module page:', errorNormalized);
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };
        void checkModuleEnabled();
        return () => {
            cancelled = true;
        };
    }, [module, onModuleDisabled]);
    const loadModuleComponent = async (moduleName: string) => {
        try {
            const componentName = `${moduleName.charAt(0).toUpperCase() + moduleName.slice(1)}Page`;
            const component = dynamic(() => getModuleImport(moduleName).then(mod => {
                if (mod[componentName]) {
                    return { default: mod[componentName] };
                }
                throw new KorvoError(ERR_INTERNAL_500, 2, {
                    message: `Component ${componentName} not found in module ${moduleName}`,
                    status: 500,
                });
            }), {
                loading: () => (<div style={{
                        position: 'relative',
                        height: 'calc(100vh - var(--topbar-height))',
                        minHeight: 0,
                    }}>
              <LocalLoadingOverlay message={`Loading ${moduleName}...`}/>
            </div>),
                ssr: false
            });
            setModuleComponent(() => component);
            setIsLoading(false);
        }
        catch (error: unknown) {
            const errorNormalized = normalizeError(error);
            console.errorNormalized(`Failed to load module ${moduleName}:`, errorNormalized);
            setIsLoading(false);
            onModuleNotFound?.(moduleName);
        }
    };
    if (isLoading) {
        return (<div style={{
                position: 'relative',
                height: 'calc(100vh - var(--topbar-height))',
                minHeight: 0,
            }}>
        <LocalLoadingOverlay message={`Loading ${module || 'module'}...`}/>
      </div>);
    }
    if (!isEnabled) {
        return (<div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>{module.charAt(0).toUpperCase() + module.slice(1)} Module Not Available</h1>
        <p>The {module} module is not enabled in this deployment.</p>
      </div>);
    }
    if (!ModuleComponent) {
        return (<div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Module Not Found</h1>
        <p>The requested module &quot;{module}&quot; could not be loaded.</p>
      </div>);
    }
    return <ModuleComponent />;
}
